import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'

const SESSION_EXPIRY_MS = FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
const MAX_CACHE_SIZE = 50

export interface ShortlistedJourneyCacheEntry {
  journey: App.JourneyV2
  searchId?: string
  sessionId?: string
  cachedAt: number
  // The pricing mode the search that produced this journey ran under. A
  // member-priced and a public-priced fare share the same structural
  // `journeyKey`, so the mode is what tells them apart in the cache.
  isLuxPlusPriced: boolean
}

const cache = new Map<string, ShortlistedJourneyCacheEntry>()

function evictExpired() {
  const now = Date.now()
  for (const [key, entry] of cache) {
    if (now - entry.cachedAt > SESSION_EXPIRY_MS) {
      cache.delete(key)
    }
  }
}

export function cacheShortlistedJourneys(
  journeys: Array<App.JourneyV2>,
  searchId: string | undefined,
  sessionId: string | undefined,
  requestLuxPlusPricing: boolean,
) {
  evictExpired()

  const cachedAt = Date.now()
  for (const journey of journeys) {
    const journeyKey = journey.bookingInfo?.journeyKey
    if (!journeyKey) continue

    if (cache.size >= MAX_CACHE_SIZE && !cache.has(journeyKey)) {
      const oldestKey = [...cache.entries()].sort(([, a], [, b]) => a.cachedAt - b.cachedAt)[0]?.[0]
      if (oldestKey) cache.delete(oldestKey)
    }

    cache.set(journeyKey, { journey, searchId, sessionId, cachedAt, isLuxPlusPriced: requestLuxPlusPricing })
  }
}

/**
 * @param requestLuxPlusPricing When provided, only returns an entry cached
 *   under the SAME pricing mode, so a charge-eligible member never reuses a
 *   public-priced `searchId` (and a non-member never reuses a member-priced
 *   one). Charge paths MUST pass it. Omit it for display-only reads, where the
 *   pricing mode doesn't change the rendered journey.
 */
export function getCachedJourney(
  journeyKey: string,
  requestLuxPlusPricing?: boolean,
): ShortlistedJourneyCacheEntry | undefined {
  const entry = cache.get(journeyKey)
  if (!entry) return undefined
  if (Date.now() - entry.cachedAt > SESSION_EXPIRY_MS) {
    cache.delete(journeyKey)
    return undefined
  }
  if (requestLuxPlusPricing !== undefined && entry.isLuxPlusPriced !== requestLuxPlusPricing) {
    return undefined
  }
  return entry
}
